import { I18n } from '@calendly/i18n';
import { AlertWarningTriangleIcon, TimeClockCircleIcon } from '@calendly/ui/components/icons';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorTextDestructive, colorTextSecondary, primaryColorL1, colorTextActionContrast } from '@calendly/ui/theme';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { useMemo, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { useGetUserQuery } from 'common';
import { MeetingStatusIndicator } from 'components/meetings/MeetingStatusIndicator';
import { computeStatusDisplayText } from 'components/meetings/utils';
import { useMeetingsContext } from 'features/meetings/contexts/MeetingsContext';
import { useMeetingFromCache } from 'features/meetings/hooks/use_meeting_from_cache';
import { mapNodeToMeeting } from 'features/meetings/utils';
import { useScope } from 'hooks/meetings/use_scope';
import { timeNotationSelector, timezoneSelector } from 'selectors/current_user';
import { useCurrentTime } from 'shared/contexts';
import { getBadgeKey, summarizeInterval } from 'shared/utils';
import { TimeRangeContainer, PhoneContainer } from '../styles';
import { NotetakerControl } from './notetaker_control';
const bodyWithIconCss = "b17qnk3i";
const BodyWithIcon = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bodyWithIconCss, props.className)} />;
});
const getPossibleBadges = shouldUseContrast => ({
  PAYMENT_ERROR: <BodyText color={colorTextDestructive} weight="semibold" size="small">
      <BodyWithIcon>
        <AlertWarningTriangleIcon />
        {I18n.t('publisher.meetings.header_badge.payment_error')}
      </BodyWithIcon>
    </BodyText>,
  ACTION_NEEDED: <BodyText color={colorTextDestructive} weight="semibold" size="small">
      <BodyWithIcon>
        <AlertWarningTriangleIcon />
        {I18n.t('publisher.meetings.header_badge.action_needed')}
      </BodyWithIcon>
    </BodyText>,
  EXPIRED: <BodyText color={colorTextDestructive} weight="semibold" size="small">
      {I18n.t('publisher.meetings.header_badge.expired')}
    </BodyText>,
  RESCHEDULED: <BodyText color={colorTextSecondary} weight="semibold" size="small">
      {I18n.t('publisher.meetings.header_badge.rescheduled')}
    </BodyText>,
  CONFLICT: <BodyText color={colorTextDestructive} weight="semibold" size="small">
      {I18n.t('publisher.meetings.header_badge.conflict')}
    </BodyText>,
  CANCELLED: <BodyText color={colorTextDestructive} weight="semibold" size="small">
      {I18n.t('publisher.meetings.header_badge.canceled')}
    </BodyText>,
  NO_SHOW: <BodyText color={colorTextDestructive} weight="semibold" size="small">
      {I18n.t('publisher.meetings.header_badge.no_show')}
    </BodyText>,
  INVITEES_DECLINED: <BodyText color={colorTextDestructive} weight="semibold" size="small">
      <BodyWithIcon>
        <AlertWarningTriangleIcon />
        {I18n.t('publisher.meetings.header_badge.invitees_declined')}
      </BodyWithIcon>
    </BodyText>,
  IN_PROGRESS: <BodyText color={shouldUseContrast ? colorTextActionContrast : primaryColorL1} weight="semibold" size="small" as="div">
      {I18n.t('publisher.meetings.header_badge.in_progress')}
    </BodyText>,
  UP_NEXT: <BodyText color={shouldUseContrast ? colorTextActionContrast : primaryColorL1} weight="semibold" size="small" as="div">
      {I18n.t('publisher.meetings.header_badge.up_next')}
    </BodyText>
});
const timeDisplayIsCancelledCss = "tf2yx8";
const timeDisplayIsOtherCss = "t1mgzyvq";
const timeDisplayIsPhoneCss = "tajlbhz";
const timeDisplayCss = "t1vx3piu";
const TimeDisplay = forwardRef((props, ref) => {
  const {
    isCancelled,
    isOther,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(timeDisplayCss, {
    [timeDisplayIsCancelledCss]: isCancelled,
    [timeDisplayIsOtherCss]: isOther,
    [timeDisplayIsPhoneCss]: isPhone
  }, props.className)} />;
});
const timeIconCss = "t1qzfos2";
const TimeIcon = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(timeIconCss, props.className)}>
      <TimeClockCircleIcon />
    </Component>;
});
const TimeDetailsInner = (props, ref) => {
  const {
    event: initialEvent,
    otherEventsOnDay,
    shouldUseContrast,
    hasSpotlight,
    isPackageBooking = false,
    notetakerRef
  } = props;
  const isPhone = useDeviceType('isPhone');
  const timezone = useSelector(timezoneSelector);
  const timeNotation = useSelector(timeNotationSelector);
  const {
    scopeType
  } = useScope();
  const {
    data: currentUser
  } = useGetUserQuery();

  // Read latest meeting data from cache - automatically updates when cache changes
  // Use initialEvent as fallback if not in cache yet
  const cachedMeeting = useMeetingFromCache(initialEvent?.uuid, mapNodeToMeeting, {
    scopeType
  });

  // Use cached meeting if available (has latest data), otherwise fall back to initialEvent
  const event = useMemo(() => {
    if (cachedMeeting && cachedMeeting.uuid === initialEvent?.uuid) {
      // Merge cancellation data from cache if available
      return {
        ...initialEvent,
        ...cachedMeeting,
        // Preserve initialEvent's cancellation if cache doesn't have it
        cancellation: cachedMeeting.cancellation || initialEvent.cancellation
      };
    }
    return initialEvent;
  }, [cachedMeeting, initialEvent]);
  const {
    cancelled,
    startTime,
    endTime
  } = event;
  const eventStartTime = moment(startTime).tz(timezone);
  const eventEndTime = moment(endTime).tz(timezone);
  const meetingsCtx = useMeetingsContext();
  const currentTime = useCurrentTime();
  let badge = null;

  // Use MeetingStatusIndicator if realtime features are enabled
  if (meetingsCtx?.hasRealtimeFeatures) {
    const meetingStatus = meetingsCtx.getStatus(event.uuid);
    const displayText = meetingStatus ? computeStatusDisplayText(meetingStatus.status, meetingStatus.startTime, currentTime) : '';
    badge = displayText ? <MeetingStatusIndicator status={meetingStatus.status} displayText={displayText} /> : null;
  } else {
    let toDisplay = getBadgeKey(event, scopeType, {
      otherEventsOnDay,
      currentUserUuid: currentUser?.uuid
    });
    if (isPackageBooking && toDisplay === 'PAYMENT_ERROR') toDisplay = undefined;
    badge = getPossibleBadges(shouldUseContrast)[toDisplay];
  }
  const timeContent = <>
      {badge}
      <TimeDisplay isCancelled={cancelled}>
        {isPhone && <TimeIcon />}
        {summarizeInterval({
        from: eventStartTime,
        to: eventEndTime
      }, timeNotation, false)}
      </TimeDisplay>
    </>;
  return <TimeRangeContainer ref={isPhone ? undefined : ref}>
      {isPhone ? <PhoneContainer ref={ref}>{timeContent}</PhoneContainer> : timeContent}
      {isPhone && <NotetakerControl ref={notetakerRef} event={event} hasSpotlight={hasSpotlight} />}
    </TimeRangeContainer>;
};
export const TimeDetails = forwardRef(TimeDetailsInner);

require("./time_details.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./time_details.js");